import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../redux/store";
import {
  Typography,
  Box,
  IconButton,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import LoadingOverlay from "../components/LoadingOverlay";
import {
  listBatchNotifItems,
  triggerBatchNotifSending,
} from "../redux/slices/notificationsSlice";
import Toast from "../components/Toast";
import { deleteBatchNotif } from "../services/NotificationService";

const ViewBatchNotificationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    selectedBatchNotif,
    notifItemForSelectedBatch,
    totalNotifItem,
    status,
  } = useSelector((state: RootState) => state.notifications);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const idToken = localStorage.getItem("idToken");
  useDetectSession();

  const [loading, setLoading] = useState<boolean>(false);

  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [showToast, setShowToast] = useState<boolean>(false);

  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const rowsPerPageArr = [10, 15, 25, 50, 75, 100];

  const columns = [
    { field: "recipientId", headerName: "Recipient ID", width: "100px" },
    { field: "topic", headerName: "Topic", width: "90px" },
    { field: "title", headerName: "Title", width: "90px" },
    {
      field: "body",
      headerName: "Body",
      width: "120px",
    },
    {
      field: "imageUrl",
      headerName: "Image URL",
      width: "90px",
    },
    {
      field: "messageType",
      headerName: "Message Type",
      width: "80px",
    },
    {
      field: "priority",
      headerName: "Priority",
      width: "80px",
    },
    {
      field: "mobileAppDisplay",
      headerName: "Mobile App Display",
      width: "70px",
    },
    {
      field: "status",
      headerName: "Message Status",
      width: "70px",
    },
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }
  }, [idToken, navigate]);

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedBatchNotif?.id) {
      dispatch(
        listBatchNotifItems({
          cursor: currentPage,
          limit: rowsPerPage,
          batchId: selectedBatchNotif.id,
        })
      );
    }
  }, [currentPage, dispatch, rowsPerPage, selectedBatchNotif]);

  useEffect(() => {
    if (status && status === "loading") {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [status]);

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <AppContainer>
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <IconButton onClick={handleBackClick}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={pageHeading}>
            Batch Notification | {selectedBatchNotif?.fileName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              position: "absolute",
              right: 15,
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexDirection: "row",
            }}
          >
            {currentUserPermissions?.notification?.permissions?.edit &&
              selectedBatchNotif?.status === "draft" && (
                <Button
                  disabled={loading}
                  color="primary"
                  sx={[primaryBtnStyle, { marginRight: 1.5 }]}
                  onClick={async () => {
                    try {
                      await dispatch(
                        triggerBatchNotifSending(selectedBatchNotif.id)
                      );
                      setMessage(
                        "Successfully triggered batch notification sending."
                      );
                      setSeverity("success");
                      setShowToast(true);
                    } catch (error) {
                      setMessage(
                        "Failed to trigger batch notification sending."
                      );
                      setSeverity("error");
                      setShowToast(true);
                    }
                  }}
                >
                  Trigger Batch Notif
                </Button>
              )}
            {currentUserPermissions?.notification?.permissions?.delete &&
              (selectedBatchNotif?.status === "draft" ||
                selectedBatchNotif?.status === "sent") && (
                <Button
                  disabled={loading}
                  variant="outlined"
                  color="error"
                  sx={[primaryBtnStyleOutline, { borderColor: "red" }]}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const res: any = await deleteBatchNotif(
                        selectedBatchNotif.id
                      );
                      setSeverity("success");
                      setMessage(res);
                      setShowToast(true);
                      navigate(-1);
                    } catch (error) {
                      setSeverity("error");
                      setMessage("Failed to delete batch notification.");
                      setShowToast(true);
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
          </Box>
        </Box>

        <LoadingOverlay loading={status === "loading" || loading} />
        <Toast
          message={message}
          severity={severity}
          open={showToast}
          onClose={() => setShowToast(false)}
        />

        {/* Batch Notification Information */}
        <Grid
          container
          spacing={3}
          sx={{
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            marginTop: 5,
          }}
        >
          {[
            { label: "File Name", value: selectedBatchNotif?.fileName },
            { label: "Batch ID", value: selectedBatchNotif?.id },
            {
              label: "Upload Date",
              value: format(
                selectedBatchNotif?.createdOn ?? new Date(),
                "MM/dd/yyyy HH:mm:ss"
              ),
            },
            { label: "Uploaded By", value: selectedBatchNotif?.createdBy },
            {
              label: "Status",
              value: (
                <Typography
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: 500,
                    color: "primary.main",
                  }}
                >
                  {selectedBatchNotif?.status}
                </Typography>
              ),
            },
          ].map(({ label, value }, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {label}
              </Typography>
              <Typography variant="body1">{value}</Typography>
            </Grid>
          ))}
        </Grid>

        {/* Notification Items List */}
        <Grid
          container
          spacing={3}
          sx={{
            p: 2,
            marginTop: 3,
          }}
        >
          <Typography variant="h5" sx={[pageHeading, { marginBottom: 1 }]}>
            Notification Items
          </Typography>
          {notifItemForSelectedBatch.length === 0 ? (
            <Paper sx={{ width: "100%", padding: 5 }}>
              <Typography>No available records.</Typography>
            </Paper>
          ) : (
            <Paper sx={{ width: "100%" }}>
              <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.field}
                          align={"left"}
                          style={{ minWidth: column.width, fontWeight: "bold" }}
                        >
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifItemForSelectedBatch.map((row: any) => {
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                        >
                          {columns.map((column: any) => {
                            const value = row[column.field];
                            return (
                              <TableCell
                                key={`${row.id} - ${column.field}`}
                                align={"left"}
                              >
                                {value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={rowsPerPageArr}
                component="div"
                count={totalNotifItem}
                rowsPerPage={rowsPerPage}
                page={currentPage - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          )}
        </Grid>
      </Box>
    </AppContainer>
  );
};

export default ViewBatchNotificationPage;
